import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { PaymentResponseDto } from '../models/paymentResponseDto';
import { ExpectFundResponseDto } from '../models/expectResponseDto';
import { JournalResponseDto } from '../models/journalResponseDto';
import { Currency } from '../models/currency';
import { Authoriser } from '../models/authoriser';
import { PurchaseCodeDto } from '../models/PurchaseCodeLookup';
import { TransferFundResponseDto } from '../../shared/models/transferFundResponseDto';
import { RequestCountsDto } from '../../shared/models/request-countDto';
import type { ExportRows, PagedResult, UserRequestDownloadSelection, UserRequestExportRequest, UserRequestPageRequest, UserRequestTab } from '../models/paged-result';
import type { DownloadJobStatus } from '../models/download-job';

/** The request types a My Requests / Completed grid can list; the first part of the paged endpoints. */
export type HistoryRequestPath = 'payment' | 'expect-fund' | 'transfer-fund' | 'journal';

@Injectable({
    providedIn: 'root'
})
export class UserRequestsService {
    constructor(private dataService: DataService) { }

    async getPaymentRequest(): Promise<PaymentResponseDto[]> {
        return await this.dataService.get<PaymentResponseDto[]>('api/userrequests/payment-requests');
    }

    async getCompletedPaymentRequests(): Promise<PaymentResponseDto[]> {
        return await this.dataService.get<PaymentResponseDto[]>('api/userrequests/payment-completed-requests');
    }

    async getPaymentRequestById(id: number): Promise<PaymentResponseDto> {
        return await this.dataService.get<PaymentResponseDto>(`api/userrequests/payment-request/${id}`);
    }

    async withdrawPaymentRequest(id: number): Promise<string> {
        return await this.dataService.put(`api/userrequests/payment-request/withdraw?id=${id}`, {});
    }

    async getTransferFunds(): Promise<TransferFundResponseDto[]> {
        return await this.dataService.get<TransferFundResponseDto[]>('api/userrequests/transfer-funds');
    }

    async getCompletedTransferFunds(): Promise<TransferFundResponseDto[]> {
        return await this.dataService.get<TransferFundResponseDto[]>('api/userrequests/transfer-completed-requests');
    }

    async withdrawTransferRequest(id: number): Promise<string> {
        return await this.dataService.put(`api/userrequests/transfer-request/withdraw?id=${id}`, {});
    }

    async getPartnersList(): Promise<Authoriser[]> {
        return await this.dataService.get(`api/userrequests/partners-list`);
    }

    async getAllCurrencies(): Promise<Currency[]> {
        return await this.dataService.get(`api/userrequests/currencies`);
    }

    async getJournalRequest(): Promise<JournalResponseDto[]> {
        return await this.dataService.get<JournalResponseDto[]>('api/userrequests/journal-request');
    }

    async getCompletedJournalRequests(): Promise<JournalResponseDto[]> {
        return await this.dataService.get<JournalResponseDto[]>('api/userrequests/journal-completed-request');
    }

    async withdrawJournalRequest(id: number): Promise<string> {
        return await this.dataService.put(`api/userrequests/journal-request/withdraw?id=${id}`, {});
    }

    getPurchaseCodeLookup(): Promise<PurchaseCodeDto[]> {
        return this.dataService.get<PurchaseCodeDto[]>(`api/userrequests/purchasecodes`);
    }

    async getExpectFund(): Promise<ExpectFundResponseDto[]> {
        return await this.dataService.get<ExpectFundResponseDto[]>('api/userrequests/expect-fund');
    }

    async getCompletedExpectFunds(): Promise<ExpectFundResponseDto[]> {
        return await this.dataService.get<ExpectFundResponseDto[]>('api/userrequests/expect-fund-completed');
    }

    async withdrawExpectFund(id: number): Promise<string> {
        return await this.dataService.put(`api/userrequests/expect-fund/withdraw?id=${id}`, {});
    }

    async getRequestCounts(): Promise<RequestCountsDto> {
        return await this.dataService.get<RequestCountsDto>('api/userrequests/request-counts');
    }

    /** Gets one server-side page of a My Requests or Completed grid; the caller is identified by the bearer token. */
    async searchRequests<TRow>(type: HistoryRequestPath, request: UserRequestPageRequest): Promise<PagedResult<TRow>> {
        return await this.dataService.post<PagedResult<TRow>>(`api/userrequests/${type}/search`, request);
    }

    /** Gets one chunk of the rows an Excel export needs. */
    async getExportRows<TRow>(type: HistoryRequestPath, request: UserRequestExportRequest): Promise<ExportRows<TRow>> {
        return await this.dataService.post<ExportRows<TRow>>(`api/userrequests/${type}/export-rows`, request);
    }

    /** Gets every value of a data-driven column the caller may see, for the checkbox filter list. */
    async getFilterValues(type: HistoryRequestPath, tab: UserRequestTab, field: string): Promise<string[]> {
        return await this.dataService.get<string[]>(`api/userrequests/${type}/filter-values?tab=${tab}&field=${encodeURIComponent(field)}`);
    }

    /**
     * Starts a background ZIP download of the selected requests, however many there are. The server answers at once;
     * progress and the finished ZIP are followed through the DownloadJobService.
     */
    async startDownload(selection: UserRequestDownloadSelection): Promise<DownloadJobStatus> {
        return await this.dataService.post<DownloadJobStatus>('api/userrequests/downloads', selection);
    }
}
